import React, { useState, useEffect, useRef } from "react"; 
import StepItem from "./StepItem";
import "./FGItemCard.css";
import { rtdb } from "../firebase";
import { ref, get, update } from "firebase/database";
import { useProgress } from '../pages/ProgressContext';

function FGItemCard({ fg, onNavigate, isHighlighted, refreshData }) {
  // ⚡ Fixed: Removed unused 'searchQuery' declaration to resolve ESLint warning
  const { expandedCards, toggleCardExpansion } = useProgress() || {};
  
  const isExpanded = expandedCards?.has(fg.fgNumber) || false;

  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingDots, setLoadingDots] = useState(".");

  const allStages = ["Receive", "Treat", "Paint", "Pack", "Deliver"];

  const cardRef = useRef(null);

  useEffect(() => {
    if (isHighlighted && cardRef.current) {
      const scrollTimeout = setTimeout(() => {
        cardRef.current.scrollIntoView({ 
          behavior: "smooth", 
          block: "center" 
        });
        
        cardRef.current.classList.add('highlight-flash');
      }, 300); 

      return () => clearTimeout(scrollTimeout);
    }
  }, [isHighlighted]);

  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(() => {
      setLoadingDots(prev => (prev.length >= 5 ? "." : prev + "."));
    }, 400);
    return () => clearInterval(interval);
  }, [isProcessing]);

  const toggleExpand = () => {
    toggleCardExpansion(fg.fgNumber);
  };

  const handleStepClick = (nodeName) => {
    if (!onNavigate) return;
    onNavigate(nodeName, fg.fgNumber);
  };

  const handleAutoComplete = async (e) => {
    e.stopPropagation(); 
    if (!window.confirm(`Auto-complete all missing stages for ${fg.fgNumber}?`)) return;

    setIsProcessing(true);

    const updates = {};
    let receiveQty = fg.quantity || "";
    let receiveQR = "";

    // 🔹 Extract the last 7 digits/characters from the fgNumber safely
    const derivedSuffix = fg.fgNumber ? String(fg.fgNumber).slice(-7) : "";

    try {
      const receiveRef = ref(rtdb, `/Receive/${fg.fgNumber}`);
      const snapshot = await get(receiveRef);

      if (snapshot.exists()) {
        const receiveData = snapshot.val();
        receiveQty = receiveData.quantity || receiveQty;
        receiveQR = receiveData.qrImageUrl || "";
      }

      allStages.forEach((stage) => {
        if (!fg.steps.includes(stage)) {
          // 🔹 Set locationTag value (maps to Deliver if stage is Receive, otherwise mirrors stage name)
          const computedTag = stage === "Receive" ? "Deliver" : stage;

          let stageData = {
            fgNumber: fg.fgNumber,
            id: fg.fgNumber,
            userName: "Admin",
            verifiedTimestamp: Date.now(),
            fgSuffix: derivedSuffix,   // 🔹 Injected last 7 digits of fgNumber
            locationTag: computedTag,  // 🔹 Injected contextual location routing tag
            issue: {
              message: `${stage} auto-completed`,
              remark: `System bypass - missing stages filled by Admin`,
              reportedBy: "Admin",
              reportedTime: Date.now(),
              resolvedBy: "Admin",
              status: "open" 
            }
          };

          if (stage === "Receive") {
            stageData = { ...stageData, quantity: receiveQty || "0" };
          } else if (stage === "Treat") {
            stageData = { ...stageData, qrImageUrl: receiveQR, quantity: receiveQty };
          } else if (stage === "Paint") {
            stageData = { ...stageData, qrImageUrl: receiveQR };
          } else if (stage === "Pack") {
            stageData = { ...stageData, cbNo: true, qrImageUrl: receiveQR, quantity: receiveQty, note: "Auto-packed" };
          } else if (stage === "Deliver") {
            stageData = { ...stageData, qrImageUrl: receiveQR };
          }

          updates[`/${stage}/${fg.fgNumber}`] = stageData;
          
          const stageMap = { "Receive": "s1", "Treat": "s2", "Paint": "s3", "Pack": "s4", "Deliver": "s5" };
          updates[`/Progress/${fg.fgNumber}/${stageMap[stage]}`] = true;
        }
      });

      if (Object.keys(updates).length > 0) {
        await update(ref(rtdb), updates);
        await new Promise(resolve => setTimeout(resolve, 500));
        if (refreshData) await refreshData();
      }
    } catch (error) {
      console.error("Auto-complete failed:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div 
        id={`fg-card-${fg.fgNumber}`}          
        data-fg={fg.fgNumber}                        
        ref={cardRef} 
        className={`fg-item-card ${isHighlighted ? "highlighted-fg is-highlighted" : ""}`}
        style={{
        transition: "all 0.3s ease-in-out",
        border: isHighlighted ? "2px solid #0ea5e9" : "1px solid #e5e7eb",
        backgroundColor: isHighlighted ? "#f0f9ff" : "#ffffff", 
        borderRadius: "12px",
        margin: "12px auto",
        width: "100%",
        maxWidth: "800px",
        boxShadow: isHighlighted ? "0 4px 12px rgba(14, 165, 233, 0.15)" : "0 2px 4px rgba(0,0,0,0.02)"
      }}
    >
      <div className="fg-header" onClick={toggleExpand} style={{ cursor: "pointer", padding: "16px" }}>
        <div className="fg-header-top" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span className="fg-number" style={{ fontWeight: "700", color: "#1f2937", fontSize: "1rem", display: "flex", alignItems: "center", gap: "8px" }}>
            {fg.fgNumber}
            {fg.hasAnyOpenIssue && (
              <span title="Pending Action" style={{ color: "#ef4444", fontSize: "1.1rem", margin: "0 5px"}}>⚠</span>
            )}
            {isHighlighted && "📍"}
          </span>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {fg.percentage < 100 && (
              <button
                onClick={handleAutoComplete}
                disabled={isProcessing}
                className="auto-complete-btn"
                style={{
                  padding: "6px 12px",
                  fontSize: "0.7rem",
                  fontWeight: "600",
                  backgroundColor: "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: isProcessing ? "not-allowed" : "pointer",
                  opacity: isProcessing ? 0.7 : 1
                }}
              >
                {isProcessing ? `Processing${loadingDots}` : "Auto 100%"}
              </button>
            )}
            <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>{fg.time || "-"}</span>
            <span style={{ color: fg.percentage === 100 ? "#10b981" : "#0ea5e9", fontWeight: "700" }}>{fg.percentage}%</span>
            {fg.stepsList.length > 0 && (
              <span style={{ 
                transition: "transform 0.2s", 
                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", 
                display: "inline-block",
                color: "#9ca3af" 
              }}>▶</span>
            )}
          </div>
        </div>
        <div className="fg-part" style={{ fontSize: "0.8rem", color: "#4b5563", marginTop: "4px" }}>
          {fg.part || "No Description"}
        </div>
      </div>

      {isExpanded && fg.stepsList.length > 0 && (
        <div className="fg-steps" style={{ padding: "8px 16px 16px", borderTop: "1px solid #f3f4f6" }}>
          {fg.stepsList.map((stepItem, idx) => {
            if (stepItem.type === "gap") return <div key={idx} style={{ height: "8px" }} />;
            const nodeName = stepItem.step;
            const remarkData = fg.nodeRemarks[nodeName]; 
            const hasRemark = !!remarkData?.note || !!remarkData?.issue; 
            const isNew = remarkData?.isNew;
            const hasOpenIssue = remarkData?.hasOpenIssue;

            return (
              <StepItem
                key={idx}
                stepText={nodeName}
                hasRemark={hasRemark}
                isNew={isNew}
                hasOpenIssue={hasOpenIssue} 
                onClick={() => handleStepClick(nodeName)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default FGItemCard;